import { getListingPrice } from '@components/PropertyDetails/helper'
import { LISTING_STATUS } from '@constants/property'
import WagtailBlocksSection from '@global/Blocks/WagtailBlocksSection'
import { PROPPS_ENDPOINT } from 'lib/variables'
import { NextSeo } from 'next-seo'
import Head from 'next/head'
import React, { useContext, useEffect } from 'react'
import PropertyDetails from '../components/PropertyDetails'
import SuburbApiDataFromName from '../components/PropertySearchResults/SuburbProfile/helpers'
import { OfficeSiteContext } from '../context/OfficeSiteContext'
import { useSiteSettingsContext } from '../context/SiteSettingsContext'
import { UserContext } from '../context/UserContext'
import { logEvent } from '../lib/analyticsService'
import { createFavourite } from '../lib/api-services/authService'
import { hitTypes, logHit } from '../lib/api-services/hitsService'
import {
  getProperties,
  getProperty,
} from '../lib/api-services/propertiesService'
import {
  ACTIVEPIPE_COOKIE,
  getCookie,
  JWT_COOKIE,
  SEARCHPAGE_COOKIE,
  setCookie,
} from '../lib/cookies'
import { formatDate } from '../lib/date'
import {
  getAgentDisplayName,
  propertyChannelString,
  propertyDataToLdJson,
  propertyDetailsUrl,
} from '../lib/helpers'
import { domain, HTTPStatusCode } from '../lib/variables'
import Error from './_error'

const adFenixCodes = (property, description) => {
  const agent =
    property.agents && property.agents.length ? property.agents[0] : null
  const images = property.images?.map((it) => it.image.image_800_600)

  const listingPrice = getListingPrice(property)

  return `
<!--property-bathrooms:"${property.bathrooms}"-->
<!--property-bedrooms:"${property.bedrooms}"-->
<!--property-date-listed:"${formatDate(property.created, 'yyyy-MM-dd')}"-->
<!--property-description:"${description}"-->
<!--property-message:""-->
<!--property-home-type:"${property.category}"-->
<!--property-images:"${images?.[0] || ''}"-->
${images?.[1] ? `<!--property-images:"${images[1]}"-->` : ''}
<!--property-listing-type:"${property.property_class}"-->
<!--property-exclusive:"${property.offmarket ? 'true' : 'false'}"-->
<!--property-offmarket-status:"${property.offmarket_status || ''}"-->
<!--property-parking:"${property.parking}"-->
<!--property-price:"${listingPrice}"-->
<!--property-sqm:"${property.area}"-->
<!--property-status:"${property.status}"-->
<!--property-address:"${property.address_street_display}"-->
<!--property-area-large:"${property.address_region}"--> 
<!--property-area-small:"${property.address_suburb}"--> 
<!--property-latitude:"${property.location?.coordinates[1]}"-->
<!--property-longitude:"${property.location?.coordinates[0]}"-->
<!--property-postcode:"${property.address_postcode}"-->
<!--property-agent-name:"${getAgentDisplayName(agent).fullName}"-->
<!--property-agent-email:"${agent?.email || ''}"-->
<!--property-agent-image:"${agent?.profile_image?.url || ''}"-->
<!--property-agent-mobile:"${agent?.phone || ''}"-->
<!--property-agent-website:"${agent?.url ? `${domain}${agent.url}` : ''}"-->
<!--property-agent-title:"${agent?.job_title || ''}"-->
<!--property-office:${property.office?.title || ''}-->
`
}

const PropertyDetailsPage = ({
  propertyData,
  similarProperties,
  suburbData,
  backToResultsLink,
  token,
  apContact,
  statusCode,
  showOffice,
}) => {
  if (statusCode === 404) {
    return (
      <Error pageData={{ statusText: HTTPStatusCode[404], statusCode: 404 }} />
    )
  }

  const { currentSite, enableSite, resetSite } = useContext(OfficeSiteContext)
  const { setCurrentPage, favouriteProperties } = useContext(UserContext)
  const siteSettings = useSiteSettingsContext()

  useEffect(() => {
    if (
      propertyData?.office &&
      showOffice &&
      currentSite?.officeId !== propertyData.office.id
    ) {
      const { id, title, path } = propertyData.office
      enableSite(id, title, path, {})
    } else {
      resetSite()
    }

    return resetSite()
  }, [showOffice])

  useEffect(() => {
    // GA: log property view in Google Analytics
    logEvent(
      'Property',
      `${propertyData.address_streetNumber} ${propertyData.address_street}, ${propertyData.address_suburb} - ${propertyData.id}`,
      'Property Details',
      2
    )

    // set "current page" to property details - for header printing
    setCurrentPage('property-details')
    return () => setCurrentPage('')
  }, [])

  const isOffmarket =
    propertyData?.offmarket_status &&
    (propertyData.offmarket_status === 'current' ||
      propertyData.offmarket_status === 'sold')

  let unlocked =
    isOffmarket && favouriteProperties.indexOf(propertyData.id) != -1

  useEffect(() => {
    // handling offmarket listing data
    if (token && isOffmarket) {
      createFavourite(token, 'listing', propertyData.id)
      logHit({
        id: propertyData.id,
        type: hitTypes.OFFMARKET_VIEW,
      })
    } else if (apContact) {
      setCookie(ACTIVEPIPE_COOKIE, true, 36500) // expires in 100 years
      logHit({
        id: propertyData.id,
        type: hitTypes.OFFMARKET_VIEW_NOLOGIN,
      })
    }
  }, [propertyData.id, isOffmarket, unlocked, apContact, token])

  // displaying offmarket listings
  if ((token && isOffmarket && !unlocked) || apContact) unlocked = true

  let infoPanels = []
  if (propertyData.listing_type == 'sale') {
    infoPanels =
      siteSettings?.settings?.contentsettings?.buyer_information_blocks
  } else if (propertyData.listing_type == 'lease') {
    infoPanels =
      siteSettings?.settings?.contentsettings?.renter_information_blocks
  }
  const footerCTA =
    siteSettings?.settings?.contentsettings?.appraisal_call_to_action[0]
  // render

  const propertyDetailsBlocks =
    siteSettings?.settings?.contentsettings?.property_details_blocks?.map(
      (it) => ({ type: it.type, value: { ...it } })
    ) ?? []

  const propertyRoute =
    propertyData.get_absolute_url || propertyDetailsUrl(propertyData)
  const canonicalURL = `${domain}${propertyRoute || ''}`

  const ldJson = propertyDataToLdJson(propertyData, canonicalURL)

  const isSoldProperty =
    propertyData &&
    propertyData.listing_type === 'sale' &&
    propertyData.status === 'sold'

  const isLeasedProperty =
    propertyData &&
    propertyData.listing_type === 'lease' &&
    propertyData.status === 'leased'

  const addressDisplay = `${propertyData?.address_street_display}, ${propertyData?.address_suburb}, ${propertyData?.address_state} ${propertyData?.address_postcode}`
  const metaAddress = propertyData?.address_display_string
    ? propertyData?.address_display_string
    : addressDisplay

  const propps_enabled = propertyData?.office?.propps_integration
  const propps_office_id = propertyData?.office?.propps_office_id
  const officeName = propertyData?.office?.office_name
    ? propertyData?.office?.office_name.indexOf('Barry Plant') === -1
      ? `Barry Plant ${propertyData?.office?.office_name}`
      : propertyData?.office?.office_name
    : 'Barry Plant'
  const metaDescriptionForSold = `${metaAddress} has been sold by ${officeName}. Looking to sell your property in ${propertyData?.address_suburb}? Contact Barry Plant today`
  const metaDescriptionForLeased = `${metaAddress} was leased by ${officeName} on ${formatDate(
    propertyData.dateAvailable,
    'MM-dd-yyyy'
  )}. Find out more at https://barryplant.com.au`

  const formatMetaDescription = (propertyData) => {
    switch (propertyData.property_class) {
      case 'commercial':
        return `Commercial property for ${propertyData.listing_type === 'lease' ? 'rent' : 'sale'
          } at ${propertyData.address_display_string
          }. You can find out more and browse other properties for ${propertyData.listing_type === 'lease' ? 'rent' : 'sale'
          } at barryplant.com.au`
      default:
        return `${propertyData.bedrooms ? `${propertyData.bedrooms} bedroom ` : ''
          }${propertyData.category} for ${propertyData.listing_type === 'lease' ? 'rent' : 'sale'
          } at ${propertyData.address_display_string
          }. You can find out more and browse other properties for ${propertyData.listing_type === 'lease' ? 'rent' : 'sale'
          } at barryplant.com.au`
    }
  }

  return (
    <React.Fragment>
      <NextSeo
        title={`${propertyData.address_display_string
          ? propertyData.address_display_string
          : propertyData.address_suburb
          } - ${propertyChannelString(propertyData)}`}
        description={
          isSoldProperty
            ? metaDescriptionForSold
            : isLeasedProperty
              ? metaDescriptionForLeased
              : formatMetaDescription(propertyData)
        }
        canonical={canonicalURL}
        openGraph={{
          title: `${propertyData.address_display_string
            } - ${propertyChannelString(propertyData)}`,
          description: isLeasedProperty
            ? metaDescriptionForLeased
            : `${propertyChannelString(propertyData)} - ${propertyData.headline
            }`,
        }}
        noindex={false}
        noarchive={false}
      />
      <Head>
        {propertyData.address_street_display && (
          <meta
            property='og:street-address'
            content={`${propertyData.address_street_display.trim()}`}
          />
        )}
        {propertyData.address_suburb_display && (
          <meta property='og:locality' content={propertyData.address_suburb} />
        )}
        <meta property='og:region' content={propertyData.address_state} />
        <meta
          property='og:postal-code'
          content={propertyData.address_postcode}
        />
        <meta property='og:url' content={canonicalURL} />
        {propertyData.images && propertyData.images.length > 0 && (
          <meta
            property='og:image'
            content={propertyData.images[0].image.image}
          />
        )}
        <script
          type='application/ld+json'
          dangerouslySetInnerHTML={{ __html: JSON.stringify(ldJson) }}
        />
        {propps_enabled && propps_office_id && (
          <script
            dangerouslySetInnerHTML={{
              __html: `(function() {
            if (document.querySelector('script[src*="propps.com"]')) return;
            var w=window,d=document,P=Promise,e='script',o='OfferKit',x='${PROPPS_ENDPOINT}',s;
            w[o]=w[o]||{exec:function(){var a=Array.from(arguments);return new P(function(rs,rj){w[o].q.push({rs,rj,a})})},q:[]};
            s=d.createElement(e);s.defer=1;s.src=x;d.head.appendChild(s);
            window.OfferKit.exec('init', { appId: '${propps_office_id}' });
          })();`,
            }}
          />
        )}
      </Head>
      <PropertyDetails
        data={propertyData}
        similarProperties={similarProperties}
        suburbData={suburbData}
        backToResultsLink={backToResultsLink}
        infoPanels={infoPanels}
        footerCTA={footerCTA}
        offmarketUnlocked={unlocked}
        imgPath='/static'
      />

      {propertyDetailsBlocks.length > 0 && (
        <WagtailBlocksSection blocks={propertyDetailsBlocks} />
      )}

      {/* Adfenix tracking codes */}
      <div
        dangerouslySetInnerHTML={{
          __html: adFenixCodes(
            propertyData,
            isSoldProperty
              ? metaDescriptionForSold
              : isLeasedProperty
                ? metaDescriptionForLeased
                : formatMetaDescription(propertyData)
          ),
        }}
      />
    </React.Fragment>
  )
}

PropertyDetailsPage.getInitialProps = async (ctx) => {
  const {
    query: { id, uniqueId, apContact, showOffice },
    asPath,
  } = ctx
  const { res } = ctx
  let props = { propertyData: null, similarProperties: [] }

  if (uniqueId) {
    // UniqueID in the slug means it's a legacy URL and needs to redirect
    const apiResponse = await getProperties({
      uniqueID: uniqueId,
      fields: 'slug,id,listing_type',
    })
    if (apiResponse.results && apiResponse.results.length) {
      // redirect to the first match
      const propertyData = apiResponse.results[0]
      let route = propertyDetailsUrl(propertyData)

      // If there's query params we should pass through
      if (
        asPath.indexOf('?') != -1 &&
        asPath.indexOf('?') != asPath.length - 1
      ) {
        route += '?' + asPath.split('?')[1]
      }
      if (route) {
        res.writeHead(301, {
          Location: route,
        })
        res.end()
      }
    }
  }

  if (!id) {
    return {
      statusCode: 404,
    }
  }

  const token = getCookie(JWT_COOKIE, ctx)
  props.token = token

  let params = {}
  const apCookie = getCookie(ACTIVEPIPE_COOKIE, ctx)
  if (apContact || apCookie) {
    params.apContact = 1 // this is to show offmarket listings for activepipe referrals
    props.apContact = true
  }
  const response = await getProperty(id, token, params)

  if (!response.id) {
    return {
      statusCode: 404,
    }
  } else {
    if (
      asPath !== response.get_absolute_url
    ) {
      res.writeHead(301, {
        Location: response.get_absolute_url,
      })
      res.end()
    } else {
      props.propertyData = response
    }
  }

  const { propertyData } = props
  const similarPropertiesSpecs = {
    status: propertyData?.status,
    listing_type: propertyData.listing_type,
    address_suburb: propertyData.address_suburb,
    excludeId: propertyData.id,
    per_page: 5,
  }
  const similarProperties = getProperties(similarPropertiesSpecs)
  similarProperties.results
    ? (props.similarProperties = similarProperties.results)
    : null

  props.suburbData = await SuburbApiDataFromName(propertyData.address_suburb)

  props.backToResultsLink = getCookie(SEARCHPAGE_COOKIE, ctx)
  props.showOffice = showOffice && showOffice.toLowerCase() == 'true'
  return props
}

export default PropertyDetailsPage
